import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Phone from 'lucide-react/dist/esm/icons/phone';
import Mail from 'lucide-react/dist/esm/icons/mail';
import MapPin from 'lucide-react/dist/esm/icons/map-pin';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Send from 'lucide-react/dist/esm/icons/send';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import Loader from 'lucide-react/dist/esm/icons/loader';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';

const ContactPage = () => {
  const location = useLocation();
  const formTopRef = useRef(null);
  
  useEffect(() => {
    document.documentElement.style.scrollPaddingTop = '120px';
    
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    return () => {
      document.documentElement.style.scrollPaddingTop = '0px';
    };
  }, [location]);

  // Load Turnstile script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    website: '', // Honeypot field
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const scrollToFormTop = () => {
    if (formTopRef.current) {
      const yOffset = -120;
      const element = formTopRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Get Turnstile token
      const turnstileResponse = document.querySelector('[name="cf-turnstile-response"]');
      const token = turnstileResponse ? turnstileResponse.value : null;

      if (!token) {
        throw new Error('Please complete the security verification');
      }

      const response = await fetch('https://omc-contact-form.adaptivepty.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          turnstileToken: token,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      setIsSubmitted(true);
      
      setTimeout(() => {
        scrollToFormTop();
      }, 100);

      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
          website: '',
        });
        // Reset Turnstile
        if (window.turnstile) {
          window.turnstile.reset();
        }
      }, 10000);

    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again or email us directly.');
      
      setTimeout(() => {
        scrollToFormTop();
      }, 100);

      // Reset Turnstile on error
      if (window.turnstile) {
        window.turnstile.reset();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Head Office - Johannesburg',
      details: ['3 North Close', 'Sunninghill Estate, 2191', 'Johannesburg, South Africa'],
    },
    {
      icon: Phone,
      title: 'Phone Numbers',
      details: ['+27 10 001 1234', '+27 11 807 3204', '+27 73 522 9567', '+27 78 423 2586'],
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['admin@omcca.co.za'],
    },
    {
      icon: Clock,
      title: 'Office Hours',
      details: ['Monday to Friday', '08:00 - 16:00'],
    },
  ];

  const services = [
    'Audit & Assurance',
    'Internal Audit',
    'Forensic Investigations',
    'IT Risk & Advisory',
    'Business Consulting',
    'Public Sector Solutions',
    'Venture Capital',
    'Other',
  ];

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        .cf-turnstile {
          margin: 0 auto;
        }
        .cf-turnstile iframe {
          margin: 0 auto !important;
        }
      `}</style>
      
      <section className="bg-gradient-to-br from-primary to-secondary py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div className="text-white" {...fadeInUp}>
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold leading-snug md:leading-normal lg:leading-[1.25] mb-6">
                <span className="text-cyan-400">Your Success</span> Starts with a Conversation
              </h1>
              <p className="text-xl md:text-xl text-blue-100 max-w-4xl mx-auto mb-10">
                Connect with our specialists to explore tailored solutions that meet your unique needs.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://media.phst.co.za/file/omc/omc-contact.jpg?w=600&h=400&fit=crop"
                alt="Let's Connect"
                className="rounded-2xl shadow-xl w-full"
              />
              <div className="absolute inset-0 bg-[#0164CD] opacity-10 rounded-2xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div id="contact-form">
              <motion.div {...fadeInUp}>
                <div ref={formTopRef}>
                  <h2 className="text-3xl font-bold text-gray-900 mb-8">Send Us a Message</h2>
                </div>
                
                {isSubmitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-green-800 mb-2">
                      Message Sent Successfully!
                    </h3>
                    <p className="text-green-600">
                      Thank you for contacting us. We have received your message and will respond within 48 hours.
                    </p>
                  </div>
                ) : (
                  <>
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-red-800 mb-1">Error Sending Message</h4>
                          <p className="text-sm text-red-600">{error}</p>
                          <p className="text-sm text-red-600 mt-2">
                            You can also email us directly at{' '}
                            <a href="mailto:admin@omcca.co.za" className="underline font-semibold">
                              admin@omcca.co.za
                            </a>
                          </p>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Your full name"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="your.email@example.com"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="+27 XX XXX XXXX"
                          />
                        </div>
                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                            Company/Organisation
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Your company name"
                          />
                        </div>
                      </div>

                      {/* Honeypot field - hidden from users */}
                      <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input
                          type="text"
                          id="website"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          tabIndex="-1"
                          autoComplete="off"
                        />
                      </div>

                      <div>
                        <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                          Service of Interest
                        </label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Select a service</option>
                          {services.map((service, index) => (
                            <option key={index} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                          Message *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={6}
                          value={formData.message}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="Please describe your requirements or ask any questions..."
                        />
                      </div>

                      {/* Turnstile Widget - Single instance */}
                      <div className="flex justify-center">
                        <div 
                          className="cf-turnstile" 
                          data-sitekey="0x4AAAAAAB-aimTH4tuB8TR7"
                          data-theme="light"
                          data-size="normal"
                        ></div>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#0383ED] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1A95FF] transition-colors flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader className="mr-2 h-5 w-5 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-5 w-5" />
                            Send Message
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </motion.div>
            </div>

            <motion.div
              className="lg:pl-8"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Contact Information</h2>
              <div className="space-y-8">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-primary text-white rounded-lg">
                        <info.icon className="h-6 w-6" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{info.title}</h3>
                      <div className="space-y-1">
                        {info.details.map((detail, detailIndex) => (
                          <p key={detailIndex} className="text-gray-600">
                            {info.icon === Phone ? (
                              <a href={`tel:${detail}`} className="hover:text-primary transition-colors">
                                {detail}
                              </a>
                            ) : info.icon === Mail ? (
                              <a
                                href={`mailto:${detail}`}
                                className="hover:text-primary transition-colors"
                              >
                                {detail}
                              </a>
                            ) : (
                              detail
                            )}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;